/**
 * Convert 0-based row and column indices to Excel cell reference format (e.g., A1, B2, AA10)
 * @param row - 0-based row index (DataFrame row index)
 * @param col - 0-based column index
 * @param hasHeaderRow - Whether the Excel file has a header row (default: true)
 *                       When true, DataFrame row 0 → Excel row 2 (row 1 is header)
 *                       When false, DataFrame row 0 → Excel row 1
 * @returns Excel cell reference string (e.g., "A1", "B2", "AA10")
 */
export function convertRowColToExcelRef(
  row: number,
  col: number,
  hasHeaderRow: boolean = true
): string {
  // Convert column number to letters (0 = A, 1 = B, ..., 25 = Z, 26 = AA, etc.)
  let colLetters = "";
  let colNum = col;

  // Excel columns are 1-based, so add 1
  colNum += 1;

  while (colNum > 0) {
    const remainder = (colNum - 1) % 26;
    colLetters = String.fromCharCode(65 + remainder) + colLetters;
    colNum = Math.floor((colNum - 1) / 26);
  }

  // Excel rows are 1-based
  // If there's a header row, DataFrame row 0 → Excel row 2 (row 1 is header)
  // So we add 2 instead of 1
  const rowNum = hasHeaderRow ? row + 2 : row + 1;

  return `${colLetters}${rowNum}`;
}

/**
 * Convert Excel cell reference (e.g., "A1", "B2") to 0-based row and column indices
 * @param cellRef - Excel cell reference string (e.g., "A1", "B2")
 * @returns Object with row and col as 0-based indices
 */
export function convertExcelRefToRowCol(cellRef: string): {
  row: number;
  col: number;
} {
  const match = cellRef.match(/^([A-Z]+)(\d+)$/);
  if (!match) {
    throw new Error(`Invalid Excel cell reference: ${cellRef}`);
  }

  const colLetters = match[1];
  const rowNum = parseInt(match[2], 10);

  // Convert column letters to number
  let col = 0;
  for (let i = 0; i < colLetters.length; i++) {
    col = col * 26 + (colLetters.charCodeAt(i) - 64);
  }

  // Convert to 0-based indices
  return {
    row: rowNum - 1,
    col: col - 1,
  };
}
